import React from 'react';
import { TFunction } from 'i18next';
import { GridColDef, GridRenderCellParams, GridActionsCellItem } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { Tooltip, Box } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

// Helper functions to extract FHIR data
export const getBirthNumber = (identifiers: any[]): string => {
  if (!identifiers || identifiers.length === 0) return '-';
  const birthNumberId = identifiers.find(
    (id) => id.system === 'http://hl7.cz/fhir/NamingSystem/cz-rcis',
  );
  return birthNumberId?.value || '-';
};

export const getInsuranceNumber = (identifiers: any[]): string => {
  if (!identifiers || identifiers.length === 0) return '-';
  const insuranceId = identifiers.find(
    (id) => id.system === 'http://hl7.cz/fhir/NamingSystem/cz-health-insurance',
  );
  return insuranceId?.value || '-';
};

export const getFullName = (names: any[]): string => {
  if (!names || names.length === 0) return '-';
  const name = names[0];
  const given = name.given ? name.given.join(' ') : '';
  const family = name.family || '';
  return `${family}, ${given}`.replace(/\s+/g, ' ').trim() || '-';
};

export const getGivenName = (names: any[]): string => {
  if (!names || names.length === 0) return '-';
  const name = names[0];
  return name.given ? name.given.join(' ') : '-';
};

export const getFamilyName = (names: any[]): string => {
  if (!names || names.length === 0) return '-';
  return names[0].family || '-';
};

export const getPhone = (telecoms: any[]): string => {
  if (!telecoms || telecoms.length === 0) return '-';
  const phones = telecoms.filter((t) => t.system === 'phone');
  return phones.length > 0 ? phones.map((p) => p.value).join(', ') : '-';
};

export const getEmail = (telecoms: any[]): string => {
  if (!telecoms || telecoms.length === 0) return '-';
  const emails = telecoms.filter((t) => t.system === 'email');
  return emails.length > 0 ? emails.map((e) => e.value).join(', ') : '-';
};

export const getPhones = (telecoms: any[]): string[] => {
  if (!telecoms || telecoms.length === 0) return [];
  return telecoms.filter((t) => t.system === 'phone').map((p) => p.value);
};

export const getEmails = (telecoms: any[]): string[] => {
  if (!telecoms || telecoms.length === 0) return [];
  return telecoms.filter((t) => t.system === 'email').map((e) => e.value);
};

export const getCity = (addresses: any[]): string => {
  if (!addresses || addresses.length === 0) return '-';
  return addresses[0].city || '-';
};

export const getStreet = (addresses: any[]): string => {
  if (!addresses || addresses.length === 0) return '-';
  const line = addresses[0].line;
  return line && line.length > 0 ? line.join(', ') : '-';
};

export const getPostalCode = (addresses: any[]): string => {
  if (!addresses || addresses.length === 0) return '-';
  return addresses[0].postalCode || '-';
};

export const getState = (addresses: any[]): string => {
  if (!addresses || addresses.length === 0) return '-';
  return addresses[0].state || '-';
};

export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'dd.MM.yyyy');
  } catch {
    return dateString;
  }
};

export const getColumns = (
  t: TFunction,
  onView: (row: any) => void,
  onEdit: (row: any) => void,
  onDelete: (row: any) => void,
): GridColDef[] => [
  {
    field: 'actions',
    headerName: t('Grid:actions'),
    type: 'actions',
    width: 100,
    hideable: false,
    getActions: (params: any) => [
      <GridActionsCellItem
        key="edit"
        icon={
          <Tooltip title={t('action.edit')}>
            <EditIcon />
          </Tooltip>
        }
        label={t('action.edit')}
        onClick={() => onEdit(params.row)}
        showInMenu={false}
      />,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={t('action.delete')}>
            <DeleteIcon />
          </Tooltip>
        }
        label={t('action.delete')}
        onClick={() => onDelete(params.row)}
        showInMenu={false}
      />,
    ],
  },
  {
    field: 'id',
    headerName: t('id'),
    type: 'number',
    width: 80,
    filterable: true,
  },
  {
    field: 'birthNumber',
    headerName: t('birthNumber'),
    width: 130,
    filterable: true,
    valueGetter: (value: any, row: any) => getBirthNumber(row.identifier),
  },
  {
    field: 'name',
    headerName: t('name'),
    width: 250,
    filterable: true,
    valueGetter: (value: any, row: any) => getFullName(row.name),
    renderCell: (params: GridRenderCellParams) => (
      <Box
        onClick={() => onView(params.row)}
        sx={{
          cursor: 'pointer',
          color: (theme) =>
            theme.palette.mode === 'dark'
              ? theme.palette.primary.light
              : theme.palette.primary.dark,
          fontWeight: 'bold',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        {getFullName(params.row.name)}
      </Box>
    ),
  },
  {
    field: 'birthDate',
    headerName: t('birthDateShort'),
    type: 'date',
    width: 120,
    filterable: true,
    valueGetter: (value: any, row: any) => {
      if (!row.birthDate) return null;
      return new Date(row.birthDate);
    },
    renderCell: (params: GridRenderCellParams) => formatDate(params.row.birthDate),
  },
  {
    field: 'gender',
    headerName: t('gender'),
    width: 100,
    type: 'singleSelect',
    valueOptions: [
      { value: 'male', label: t('genderValue.MALE') },
      { value: 'female', label: t('genderValue.FEMALE') },
      { value: 'other', label: t('genderValue.OTHER') },
      { value: 'unknown', label: t('genderValue.UNKNOWN') },
    ],
    filterable: true,
    renderCell: (params: GridRenderCellParams) => {
      const gender = params.value;
      if (!gender) return '-';
      return t(`genderValue.${gender.toUpperCase()}`);
    },
  },
  {
    field: 'city',
    headerName: t('city'),
    width: 150,
    filterable: true,
    valueGetter: (value: any, row: any) => getCity(row.address),
  },
  {
    field: 'street',
    headerName: t('street'),
    width: 200,
    filterable: true,
    valueGetter: (value: any, row: any) => getStreet(row.address),
  },
  {
    field: 'postalCode',
    headerName: t('postalCode'),
    width: 100,
    filterable: true,
    valueGetter: (value: any, row: any) => getPostalCode(row.address),
  },
  {
    field: 'state',
    headerName: t('state'),
    width: 150,
    filterable: true,
    valueGetter: (value: any, row: any) => getState(row.address),
  },
  {
    field: 'phone',
    headerName: t('phone'),
    width: 150,
    filterable: true,
    valueGetter: (value: any, row: any) => getPhone(row.telecom),
  },
  {
    field: 'email',
    headerName: t('email'),
    width: 200,
    filterable: true,
    valueGetter: (value: any, row: any) => getEmail(row.telecom),
  },
  {
    field: 'deceased',
    headerName: t('deceased'),
    width: 100,
    type: 'boolean',
    filterable: true,
    valueGetter: (value: any, row: any) => row.deceasedBoolean || false,
  },
  {
    field: 'active',
    headerName: t('active'),
    width: 100,
    type: 'boolean',
    filterable: true,
  },
];

export const GRID_PATIENTS_SETTINGS_KEY = 'patientsMUI';
